import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import MaterialIcon from './MaterialIcon';

let nextId = 0;

/**
 * A small help icon after a label, explaining it. It opens on hover, on keyboard focus, and on a
 * tap, which focuses it, and closes on leaving, blurring, Escape or a scroll. The bubble is placed
 * against the viewport rather than inside its parent, because the loan cards clip what overflows
 * them, and kept on screen at both edges. It is rendered into the body, since a transformed
 * ancestor such as an open modal would otherwise make its fixed position relative to that ancestor.
 */
export default function InfoTip({ children, label = 'What this means' }) {
	const icon = useRef(null);
	const [id] = useState(() => `info_tip_${++nextId}`);
	const [position, updatePosition] = useState(null);

	function show() {
		const rect = icon.current.getBoundingClientRect();
		const width = Math.min(260, window.innerWidth - 32);
		const left = Math.min(Math.max(16, rect.left + rect.width / 2 - width / 2), window.innerWidth - 16 - width);
		// Below the icon, unless that is the bottom of the screen.
		const vertical = rect.bottom > window.innerHeight * 0.65
			? { bottom: window.innerHeight - rect.top + 6 }
			: { top: rect.bottom + 6 };
		updatePosition({ left, width, ...vertical });
	}

	function hide() {
		updatePosition(null);
	}

	function onKeyDown(event) {
		if(event.key === 'Escape') {
			hide();
		}
	}

	useEffect(() => {
		if(!position) {
			return undefined;
		}
		window.addEventListener('scroll', hide, true);
		return () => window.removeEventListener('scroll', hide, true);
	}, [position]);

	return (
		<span className='info_tip'>
			<MaterialIcon
				ref={icon}
				className='info_tip__icon'
				tabIndex={0}
				role='button'
				aria-label={label}
				aria-describedby={id}
				onMouseEnter={show}
				onMouseLeave={hide}
				onFocus={show}
				onBlur={hide}
				onKeyDown={onKeyDown}
			>
				help_outline
			</MaterialIcon>
			{ReactDOM.createPortal(
				<span id={id} role='tooltip' className='info_tip__bubble' hidden={!position} style={position || undefined}>
					{children}
				</span>,
				document.body
			)}
		</span>
	);
}
